/* NECCST product gallery: finish/variant-aware media + mobile swipe carousel.

   Media model (P8): the snippet renders every product media item once, tagged with:
     - data-finish="wood"|"black"  (parsed from an alt-text token: [wood]/[cherry] or [black]),
     - data-variants=",<id>,<id>,"  (the media is a variant's featured_media — single-image fallback),
     - data-general="true"          (no finish token and not a variant featured media: shared).
   On load and on every `neccst:variant-change` (from neccst-buy-box.js) this script rebuilds
   the visible set:
     slots 1..N  = the current finish's media (featured first),
     slots N+1.. = the general media (shared, never swap).
   Everything else is hidden; the first visible cell becomes the spanning lead cell.

   Admin convention: give each of the 3 wood shots an alt starting with "[wood]" and each of
   the 3 black shots an alt starting with "[black]"; leave the 2 general shots' alt with no
   bracket token. For GBW-specific shots, ALSO add "[gbw]" (e.g. "[gbw][black] ...") -> they
   show only on the GBW build; GBW falls back to the plain finish shots when none are tagged
   [gbw]. (A single image assigned directly to a variant still swaps via data-variants.)

   Desktop is a CSS grid; the mobile dots are rebuilt whenever the visible set changes. */
(function () {
  // Map a finish display name (from the buy box) to the alt-text token.
  function finishToken(name) {
    if (!name) return '';
    var n = String(name).toLowerCase();
    if (n.indexOf('black') !== -1) return 'black';
    if (n.indexOf('wood') !== -1 || n.indexOf('cherry') !== -1) return 'wood';
    return '';
  }
  // Is the selected build the GBW version? (from the buy box's build/VERSION value)
  function isGbwBuild(name) {
    return !!name && String(name).toLowerCase().indexOf('gbw') !== -1;
  }

  document.querySelectorAll('[data-neccst-gallery]').forEach(function (gal) {
    var dots = gal.parentNode.querySelector('[data-neccst-gallery-dots]');
    var variantMax = parseInt(gal.getAttribute('data-variant-max'), 10) || 3;
    var generalMax = parseInt(gal.getAttribute('data-general-max'), 10);
    if (isNaN(generalMax)) generalMax = 2;

    var allCells = [].slice.call(gal.querySelectorAll('.g-cell'));
    if (!allCells.length) return;

    function cellVariantIds(cell) {
      return (cell.getAttribute('data-variants') || '').split(',').filter(function (s) { return s !== ''; });
    }

    // Build the ordered, visible set for build (GBW?) + finish token + variant id.
    // Priority for the variant-specific slots: exact build+finish match, else finish-only
    // (so GBW falls back to the finish shots when no [gbw] shots exist), else single-image
    // per-variant assignment.
    function buildSet(token, isGbw, variantId) {
      var vid = variantId != null ? String(variantId) : '';
      var exact = [];       // finish token + build match
      var finishOnly = [];  // finish token match, any build
      var variantHit = [];  // no token, but assigned to this variant
      var generalCells = [];
      allCells.forEach(function (cell) {
        if (cell.getAttribute('data-general') === 'true') { generalCells.push(cell); return; }
        var cellFinish = cell.getAttribute('data-finish') || '';
        var cellGbw = (cell.getAttribute('data-build') || '') === 'gbw';
        if (token && cellFinish === token) {
          finishOnly.push(cell);
          if (cellGbw === isGbw) exact.push(cell);
        } else if (!cellFinish && vid && cellVariantIds(cell).indexOf(vid) !== -1) {
          variantHit.push(cell);
        }
      });
      var finishCells = exact.length ? exact : (finishOnly.length ? finishOnly : variantHit);
      // featured media of the current variant first within its set
      finishCells.sort(function (a, b) {
        return (b.getAttribute('data-featured') === 'true' ? 1 : 0) - (a.getAttribute('data-featured') === 'true' ? 1 : 0);
      });
      var chosen = finishCells.slice(0, variantMax).concat(generalCells.slice(0, generalMax));
      // Guard: nothing matched (e.g. no tokens set yet) -> show the first cells unchanged.
      if (!chosen.length) chosen = allCells.slice(0, variantMax + generalMax);
      return chosen;
    }

    function render(token, isGbw, variantId) {
      var chosen = buildSet(token, isGbw, variantId);
      allCells.forEach(function (cell) {
        cell.hidden = true;
        cell.classList.remove('g-lead');
        cell.removeAttribute('data-lead');
      });
      chosen.forEach(function (cell, i) {
        cell.hidden = false;
        gal.appendChild(cell); // reorder into the chosen sequence
        if (i === 0) {
          cell.classList.add('g-lead');
          cell.setAttribute('data-lead', '');
        }
      });
      buildDots(chosen);
    }

    // ---- mobile dots ----
    var scrollHandler = null;
    function buildDots(cells) {
      if (!dots) return;
      dots.innerHTML = '';
      if (scrollHandler) { gal.removeEventListener('scroll', scrollHandler); scrollHandler = null; }
      if (cells.length < 2) return;
      cells.forEach(function (cell, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-label', 'Show image ' + (i + 1) + ' of ' + cells.length);
        if (i === 0) {
          b.classList.add('is-active');
          b.setAttribute('aria-selected', 'true');
        }
        b.addEventListener('click', function () {
          gal.scrollTo({ left: cell.offsetLeft - gal.offsetLeft, behavior: 'smooth' });
        });
        dots.appendChild(b);
      });
      var btns = [].slice.call(dots.children);
      var raf;
      scrollHandler = function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          var idx = Math.round(gal.scrollLeft / gal.clientWidth);
          idx = Math.max(0, Math.min(btns.length - 1, idx));
          btns.forEach(function (b, i) {
            var on = (i === idx);
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-selected', on ? 'true' : 'false');
          });
        });
      };
      gal.addEventListener('scroll', scrollHandler, { passive: true });
    }

    // Initial render: derive the finish token from the server-marked lead cell's data-finish,
    // and use the server-marked current variant id for the single-image fallback.
    var initialVariant = gal.getAttribute('data-current-variant') || '';
    var leadCell = gal.querySelector('.g-cell[data-lead]');
    var initialToken = leadCell ? (leadCell.getAttribute('data-finish') || '') : '';
    var initialGbw = leadCell ? (leadCell.getAttribute('data-build') === 'gbw') : false;
    render(initialToken, initialGbw, initialVariant);

    // React to buy-box variant/finish changes.
    document.addEventListener('neccst:variant-change', function (e) {
      if (!e.detail) return;
      // Prefer the buy box's stable token (works on /de where finish names are localized);
      // fall back to mapping the display name for older events.
      var tok = e.detail.finishToken || finishToken(e.detail.finish);
      render(tok, isGbwBuild(e.detail.build), e.detail.id);
    });
  });
})();
